<template>
  <li class="support" :class="{ 'is-open': isOpen }" ref="suppDropdown" @focusout="handleClickOutside">
    <button type="button" class="btn-icon support-dropdown-btn" :aria-expanded="isOpen.toString()" aria-controls="support-dropdown-menu" @click="toggleOpen">
      <i class="fas fa-question-circle" aria-hidden="true"></i>
      <span>Support</span>
      <i class="fas fa-caret-down" aria-hidden="true"></i>
    </button>
    <ul id="support-dropdown-menu" class="dropdown nested-dropdown k-menu-panel" :class="{ 'is-open': isOpen }" :hidden="!isOpen">
      <li v-if="programmeDetails && programmeDetails.knowledge_base_enabled && $route.name !== 'module_knowledge_base'"
        class="knowledge-base-link k-menu-item"
      >
        <button type="button" v-if="isProgrammePage" @click="openChooseModuleModal">
          Ask technical question
        </button>
        <router-link v-else-if="moduleId" :to="{ name: 'module_knowledge_base', moduleId }">
          Ask technical question
        </router-link>
      </li>
      <li v-if="programmeDetails" class="k-menu-item">
        <router-link :to="{ name: 'profile_questions' }">
          My questions
        </router-link>
      </li>
      <li v-if="!store.isDashboard" class="k-menu-item">
        <a href="mailto:help@cambridgespark.com?subject=Contact%20Cambridge%20Spark">
          Contact Cambridge Spark
        </a>
      </li>
      <li class="k-menu-item">
        <button type="button" @click="openHelpFor('report_bug')" :class="{active : helpKey === 'report_bug'}">
          Report a bug
        </button>
      </li>
      <li v-if="!store.isDashboard" class="k-menu-item">
        <router-link :to="{ name: 'help' }">Help pages</router-link>
      </li>
      <li v-if="!store.isDashboard" class="k-menu-item">
        <a href="mailto:safeguarding@cambridgespark.com?subject=Report%20Safeguarding%20Concern">
          Report a Safeguarding Concern
        </a>
      </li>
    </ul>
  </li>
</template>

<style scoped>
li.is-open > .btn-icon.support-dropdown-btn {
  color: var(--kate-neon-primary-text);
}

.btn-icon.support-dropdown-btn {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  gap: 14px;
  padding: 15px;
  border-radius: 0;
}

.support-dropdown-btn {
  padding: 15px;
}

.support-dropdown-btn span {
  padding: 0 5px;
}

.support-dropdown ul.dropdown.nested-dropdown {
  width: 200px;
  left: auto;
  right: calc(100% + 8px);
  top: -8px;
}

.support-dropdown ul.nested-dropdown.is-open {
  transform: translate(0, 0);
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 60vh;
}

.support-dropdown ul.nested-dropdown.is-open::-webkit-scrollbar {
  background-color: var(--kate-background-alt);
  width: 8px;
}

.support-dropdown ul.nested-dropdown.is-open::-webkit-scrollbar-track {
  box-shadow: var(--scrollbar-track);
}

.support-dropdown ul.nested-dropdown.is-open::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb);
  border-radius: 3px;
}

.support-dropdown .dropdown.nested-dropdown::before {
  display: none;
}

@media (max-width: 767px) {
  .support-dropdown ul.nested-dropdown {
    position: unset;
    display: none;
    width: 100%;
    right: auto;
  }

  .support-dropdown ul.nested-dropdown.is-open {
    transform: translate(0, 0);
    display: block;
    width: 100%;
    box-shadow: inset 0 0 5px 3px  rgb(13 12 24 / 0.9) !important;
  }
}
</style>

<script>
import useGlobalStore from '@stores/global';
import useAppProgrammeStore from '@stores/app-programme-store';
import SidebarMixin from '@mixins/sidebar-mixin';
import getOrNull from '@utils/get-or-null';
import { ASSET_TYPES } from '../../constants';

export default {
  mixins: [SidebarMixin],

  props: {
    isOpen: {
      type: Boolean,
    },
  },

  data() {
    return {
      store: useGlobalStore(),
      appProgrammeStore: useAppProgrammeStore(),
      showHelp: false,
      helpKey: undefined,
      showChooseModule: false,
      threadName: undefined,
      threadId: undefined,
      moduleAssetName: undefined,
      moduleAssetId: undefined,
      assetId: undefined,
      moduleAssetType: undefined,
      moduleAssetTypePretty: undefined,
    };
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  watch: {
    route() {
      // Update info any time the route changes and close the help dropdown (the options may change)
      this.$emit('close');
      this.reset();
    },
  },

  computed: {
    programmeId() {
      return this.store.isDashboard ? null : this.appProgrammeStore.currentProgrammeId;
    },
    isProgrammePage() {
      return this.$route.matched.some(record => record.path === '/programmes/:programmeId?/:track?');
    },
    route() {
      return this.$route;
    },
    isModulePage() {
      return this.$route.matched[0].path === '/modules/:moduleId';
    },
    programmeDetails() {
      return this.store.isDashboard ? null : this.appProgrammeStore.currentProgramme;
    },
    moduleId() {
      // Get the module id from the route not the sidebar mixin since this will return the module details of the
      // previous module when not on a module page
      if (!this.$route.params?.moduleId) {
        return undefined;
      }
      return parseInt(this.$route.params?.moduleId, 10);
    },
    moduleName() {
      if (this.moduleId && this.moduleId === this.moduleDetails?.id) {
        return this.moduleDetails?.name;
      }
      return undefined;
    },
    moduleAsset() {
      // We should generalise asset, so instead of using the following we could do this.$route.params.moduleAssetId
      // At the moment moduleAssetId is explicitly modulePakId, moduleQuizId... etc
      const asset = this.assets.find(x => {
        if (x.asset_type) {
          const routeName = ASSET_TYPES[x.asset_type].routeName;
          return x.id === parseInt(this.$route.params[`module${this.pascalCase(routeName)}Id`], 10);
        }
        return false;
      });
      if (!asset) {
        return undefined;
      }
      return asset;
    },
  },

  methods: {
    pascalCase(str) {
      return str.split('_').map(x => x.charAt(0).toUpperCase() + x.slice(1)).join('');
    },
    reset() {
      this.moduleAssetName = undefined;
      this.threadName = undefined;
      this.assetId = undefined;
      this.moduleAssetId = undefined;
      this.moduleAssetType = undefined;
      this.moduleAssetTypePretty = undefined;
      this.threadId = undefined;
    },
    setAssetInfo() {
      if (this.moduleAsset) {
        this.moduleAssetName = this.moduleAsset.name;
        this.moduleAssetId = this.moduleAsset.id;
        this.moduleAssetType = this.moduleAsset.asset_type;
        this.moduleAssetTypePretty = ASSET_TYPES[this.moduleAssetType].prettyName;
        this.assetId = this.moduleAsset[`${this.moduleAsset.asset_type}_id`];
      }
    },
    setThreadInfo() {
      // The thread crumb is always the last crumb and so doesn't have a path object
      const threadCrumb = this.$crumbs.crumbs?.find(x => (
        (this.$route.name === 'knowledge_thread' && x.active === true)
      ));
      if (threadCrumb) {
        this.threadName = getOrNull('text', threadCrumb);
      }
      if (this.$route.params.threadId) {
        this.threadId = parseInt(this.$route.params.threadId, 10);
      }
    },
    openHelpFor(key) {
      this.setAssetInfo();
      this.setThreadInfo();
      this.helpKey = key;
      this.$emit('open-help-modal', {
        helpKey: this.helpKey,
        moduleName: this.moduleName,
        moduleAssetName: this.moduleAssetName,
        threadName: this.threadName,
        programmeId: this.programmeId,
        moduleId: this.moduleId,
        assetId: this.assetId,
        moduleAssetId: this.moduleAssetId,
        moduleAssetType: this.moduleAssetType,
        moduleAssetTypePretty: this.moduleAssetTypePretty,
        threadId: this.threadId,
      });
      this.$emit('close');
    },

    toggleOpen() {
      if (this.isOpen) {
        this.$emit('close');
      } else {
        this.$emit('open');
      }
    },

    openChooseModuleModal() {
      this.$emit('open-module-modal', {
        programmeId: this.programmeId,
      });
      this.$emit('close');
    },

    handleClickOutside(event) {
      const suppDropdown = this.$refs.suppDropdown;
      const target = event.type === 'focusout' ? event.relatedTarget : event.target;
      if (suppDropdown && !suppDropdown.contains(target)) {
        this.$emit('close');
      }
    },
  },
};
</script>
